// components/errors/NotFoundPage.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Home, Search, AlertCircle, Users, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { GlassCard, GlassButton, GlassAlert } from '../ui/GlassComponents';

const NotFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('errors');
  const { t: tCommon } = useTranslation('common');

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      {/* Field pattern overlay */}
      <div className="fixed inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(90deg, transparent 49%, rgba(16, 185, 129, 0.1) 49%, rgba(16, 185, 129, 0.1) 51%, transparent 51%),
              linear-gradient(180deg, transparent 49%, rgba(16, 185, 129, 0.1) 49%, rgba(16, 185, 129, 0.1) 51%, transparent 51%)
            `,
            backgroundSize: '100px 100px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-2xl w-full space-y-6">
        {/* Main 404 card */}
        <GlassCard className="backdrop-blur-xl">
          <div className="text-center space-y-6 p-8">
            {/* 404 with icon */}
            <div className="relative mx-auto">
              <div className="text-8xl font-bold text-white/10 select-none">404</div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="bg-amber-500/10 backdrop-blur-sm border border-amber-500/30 rounded-full p-6">
                  <MapPin className="h-12 w-12 text-amber-400" />
                </div>
              </div>
            </div>

            {/* Error title */}
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                {t('not_found.title')}
              </h1>
              <p className="text-lg text-white/60">
                {t('not_found.subtitle')}
              </p>
            </div>

            {/* Error message */}
            <GlassAlert variant="warning" className="text-start">
              <div className="space-y-2">
                <p className="text-white/90 font-medium flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {t('not_found.alert_title')}
                </p>
                <p className="text-white/70 text-sm">
                  {t('not_found.description')}
                </p>
              </div>
            </GlassAlert>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <GlassButton
                variant="primary"
                size="lg"
                onClick={() => navigate('/')}
                className="group"
              >
                <Home className="h-5 w-5 me-2 group-hover:scale-110 transition-transform" />
                {tCommon('actions.home')}
              </GlassButton>

              <GlassButton
                variant="secondary"
                size="lg"
                onClick={() => navigate(-1)}
                className="group"
              >
                <span className="me-2 rtl:rotate-180 inline-block">←</span>
                {t('not_found.back_page')}
              </GlassButton>
            </div>
          </div>
        </GlassCard>

        {/* Quick links card */}
        <GlassCard className="backdrop-blur-md">
          <div className="p-6 space-y-4">
            <h3 className="text-white/90 font-semibold flex items-center gap-2">
              <Search className="h-5 w-5 text-emerald-400" />
              {t('not_found.quick_links_title')}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/30 transition-all text-start group"
              >
                <Calendar className="h-5 w-5 text-emerald-400 group-hover:scale-110 transition-transform" />
                <div>
                  <p className="text-white/90 font-medium text-sm">{t('not_found.quick_link_dashboard_title')}</p>
                  <p className="text-white/50 text-xs">{t('not_found.quick_link_dashboard_desc')}</p>
                </div>
              </button>

              <button
                onClick={() => navigate('/officials')}
                className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/30 transition-all text-start group"
              >
                <Users className="h-5 w-5 text-emerald-400 group-hover:scale-110 transition-transform" />
                <div>
                  <p className="text-white/90 font-medium text-sm">{t('not_found.quick_link_officials_title')}</p>
                  <p className="text-white/50 text-xs">{t('not_found.quick_link_officials_desc')}</p>
                </div>
              </button>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default NotFoundPage;
